import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/dialogs/confirm-dialog/confirm-dialog.component';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AccountsService, IAccountGroup } from '@services/accounts.service';
import { ILocationGroup, ILocationGroupPermissions } from '@services/organisation-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { distinctUntilChanged, filter, finalize, map, Subject, takeUntil, tap } from 'rxjs';


@Component({
	selector: 'app-add-level-permission',
	templateUrl: './add-level-permission.component.html',
	styleUrls: ['./add-level-permission.component.scss']
})
export class AddLevelPermissionComponent {

	/** Form Group */
	dialogForm: FormGroup;
	accountGroups: IAccountGroup[];
	accountSystemGroups: IAccountGroup[];
	selectedGroup: IAccountGroup | undefined;

	/** selectedAccountGUID */
	public selectedAccountGUID: string;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	public loadingGroups: boolean

	/**
	 * Default Constructor
	 * @param fb fb
	 * @param dialogRef Referense to the Dialog box
	 * @param dialogData object interface
	 * @param siteSubscriptions siteSubscriptions
	 * @param accountsService accountsService
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<AddLevelPermissionComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: any,
		private siteSubscriptions: siteSubscriptions,
		private accountsService: AccountsService,
		private globalFunctions: GlobalFunctions
	) {

		/** Get the list of Account Permission Groups */
		this.siteSubscriptions.selectedAccountGUID$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((accountGUID) => !!accountGUID)
		).subscribe({
			next: (accountGUID: any) => {
				this.selectedAccountGUID = accountGUID
				this.getAccountGroups(this.selectedAccountGUID)
			}
		})
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		this.dialogForm = this.fb.group({
			AccountGroupID: [0],
			Name: [''],
			Permissions: this.fb.array([
				this.fb.group({
					View: [false],
					Write: [false]
				})
			])
		});

		this.dialogForm.get('AccountGroupID')?.valueChanges.pipe(
			tap(() => { this.selectedGroup = undefined }),
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((value) => !!value)
		).subscribe(value => {
			if (value) {
				this.selectedGroup = <IAccountGroup>this.accountGroups.find(({ AccountGroupID }) => AccountGroupID == value)
				if (this.selectedGroup) {
					this.dialogForm.get('Name')?.setValue(this.selectedGroup.Name);
				}
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}


	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * Get Account Groups
	 * @param accountGUID
	 */
	getAccountGroups(accountGUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.accountGroups = [];
		this.accountSystemGroups = [];

		// Get Groups for the account
		this.accountsService.getAccountGroups$(accountGUID).pipe(
			tap(() => {
				this.accountGroups = []
				this.accountSystemGroups = []
				this.loadingGroups = true;
			}),
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((groups) => !!groups),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.loadingGroups = false;
			}),
			// Add map to modify group names based on SystemGroup
			map((groups: IAccountGroup[]) => {
				return groups.map(group => {
					if (group.SystemGroup) {
						return { ...group, Name: `${group.Name}*` }; // Append '*' to the Name if SystemGroup is true
					}
					return group;
				});
			}),
		).subscribe({
			next: (groups: IAccountGroup[]) => {
				try {
					this.accountGroups = <IAccountGroup[]>groups; //.filter(({ SystemGroup }) => SystemGroup == false)
				} catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error: HttpErrorResponse) => {
				if (error.status != 404) {
					console.error(error)
					this.globalFunctions.HandleError(error, "this.accountsService.getAccountGroups$: Error retrieving Account Groups")
				} else { console.error(error.error); }
			},
			//complete: () => { console.log("this.accountsService.getAccountGroups$: Complete"); }
		});
	}

}
